import type { Object3D } from 'three';
import type { SceneObject, TransitionableObjectType, TransitionContext } from '../../../types/engine.types';
import type { SceneTransitionManager } from '../scene-transition-manager.class';
import { prepareMaterialsForFadeCfn, restoreMaterialsCfn, setObjectOpacityCfn } from './material-state.cfn';
import { groupObjectsByIslandCfn } from './group-objects-by-island.cfn';
import { resolveAddedParent } from '../../scene-diff-manager';

/**
 * Animate object additions with fade in
 */
export const animateAdditionsCfn = async (
	self: SceneTransitionManager,
	objects: SceneObject[]
): Promise<void> => {
	const config = self.config;
	if (objects.length === 0 || !config.objectHolder) return;

	const { fadeInDuration, animationManager, objectHolder } = config;
	const promises: Promise<void>[] = [];

	for (const sceneObject of objects) {
		const { object, metadata } = sceneObject;
		const animId = self.nextAnimationId('addition');

		prepareMaterialsForFadeCfn(self, object);
		setObjectOpacityCfn(object, 0);

		object.userData = { ...object.userData, ...metadata };
		// Under the island it was built into, never loose on the holder — see
		// `resolveAddedParent`. A loose appliance outlives the island swap that
		// should have replaced it and renders twice.
		resolveAddedParent(objectHolder, object).add(object);

		const fadePromise = animationManager.animateOpacity(
			animId,
			0,
			1,
			fadeInDuration,
			(opacity) => setObjectOpacityCfn(object, opacity)
		).catch(() => {
			setObjectOpacityCfn(object, 1);
		});

		promises.push(fadePromise.then(() => restoreMaterialsCfn(self, object)));
	}

	await Promise.all(promises);
};

/**
 * Animate a single addition by type using transition registry
 */
export const animateAdditionByTypeCfn = async (
	self: SceneTransitionManager,
	sceneObject: SceneObject,
	animationCenter?: { x: number; y: number; z: number },
	isInitialRender = false,
	isWorldCoords = false,
	cameraPosition?: { x: number; y: number; z: number }
): Promise<void> => {
	const transitionRegistry = self.transitionRegistry;
	const config = self.config;

	const { object, metadata } = sceneObject;
	const type = metadata.type as TransitionableObjectType;
	const typeConfig = transitionRegistry[type];
	const transitionConfig = isInitialRender
		? (typeConfig?.initialize ?? typeConfig?.add)
		: typeConfig?.add;

	if (!transitionConfig?.helper) return;

	const context: TransitionContext = {
		object,
		metadata,
		islandCenter: isWorldCoords ? undefined : animationCenter,
		worldCenter: isWorldCoords ? animationCenter : undefined,
		islandIndex: metadata.islandIndex,
		animationManager: config.animationManager,
		targetPosition: object.position.clone(),
		duration: transitionConfig.duration,
		originalScale: (metadata as any).originalScale ?? object.userData?.originalScale ?? object.scale.x,
		cameraPosition,
		phaseTimings: transitionConfig.flyPhases,
		debugManager: config.debugManager
	};

	const result = transitionConfig.helper(context);

	if (type === 'appliance' && metadata.id) {
		const cutoutPromise = animateLinkedCutoutsCfn(self, object, metadata.id, transitionConfig.duration);
		await Promise.all([result.promise, cutoutPromise]);
	} else {
		await result.promise;
	}
};

/**
 * Animate cutouts linked to an appliance
 */
export const animateLinkedCutoutsCfn = async (
	self: SceneTransitionManager,
	_applianceObject: Object3D,
	applianceId: string,
	duration: number
): Promise<void> => {
	const config = self.config;
	const { objectHolder } = config;
	if (!objectHolder) return;

	const cutoutMeshes: Object3D[] = [];
	objectHolder.traverse((child) => {
		if (child.userData?.type === 'appliance-cutout' && child.userData?.applianceId === applianceId) {
			cutoutMeshes.push(child);
		}
	});

	if (cutoutMeshes.length === 0) return;

	const promises = cutoutMeshes.map((mesh) => {
		prepareMaterialsForFadeCfn(self, mesh);
		setObjectOpacityCfn(mesh, 0);

		const animId = self.nextAnimationId('cutout-fade');
		return config.animationManager.animateOpacity(
			animId,
			0,
			0.4,
			duration,
			(opacity) => setObjectOpacityCfn(mesh, opacity)
		).then(() => restoreMaterialsCfn(self, mesh));
	});

	await Promise.all(promises);
};

/**
 * Process additions by island with type ordering
 */
export const processAdditionsByIslandCfn = async (
	self: SceneTransitionManager,
	added: SceneObject[],
	islandGroups: Map<string, { center: { x: number; y: number; z: number }; objects: SceneObject[] }>
): Promise<void> => {
	const islandSequence = self.islandSequence;
	const { typeOrder, parallelGroups } = islandSequence;

	const byIsland = groupObjectsByIslandCfn(added);

	for (const [islandId, objects] of byIsland) {
		const islandData = islandGroups.get(islandId);
		const center = islandData?.center;

		for (const type of typeOrder) {
			const parallelGroup = parallelGroups?.find((g) => g.includes(type));

			if (parallelGroup) {
				const parallelObjects = objects.filter((obj) => parallelGroup.includes(obj.metadata.type as TransitionableObjectType));

				if (parallelObjects.length > 0) {
					const promises = parallelObjects.map((obj) => animateAdditionByTypeCfn(self, obj, center));
					await Promise.all(promises);
				}
				continue;
			}

			const objectsOfType = objects.filter((obj) => obj.metadata.type === type);
			if (objectsOfType.length > 0) {
				const promises = objectsOfType.map((obj) => animateAdditionByTypeCfn(self, obj, center));
				await Promise.all(promises);
			}
		}

		const remainingObjects = objects.filter((obj) => !typeOrder.includes(obj.metadata.type as TransitionableObjectType));
		if (remainingObjects.length > 0) {
			const promises = remainingObjects.map((obj) => animateAdditionByTypeCfn(self, obj, center));
			await Promise.all(promises);
		}
	}
};
